import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, Facebook, Instagram, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg, inr, media } from "@/lib/api";

export default function ReferShareModal() {
  const { shareProduct: p, setShareProduct, pct } = useApp();
  const [link, setLink] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!p) return;
    setLink(""); setErr("");
    api.post("/referrals/link", { product_id: p.id || null })
      .then((r) => setLink(window.location.origin + r.data.path))
      .catch((e) => setErr(errMsg(e)));
  }, [p]);

  if (!p) return null;
  const msg = `Found something beautiful for your puja? 🪔 I loved ${p.name ? `this ${p.name}` : "Bhakti Sakhi"} — check it out.`;
  const full = `${msg} ${link}`;
  const copy = (t, label) => { navigator.clipboard?.writeText(t); toast.success(label); };
  const open = (u) => window.open(u, "_blank", "noopener");

  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && setShareProduct(null)}>
      <DialogContent data-testid="referral-share-modal" className="max-w-md overflow-hidden rounded-3xl border-[#E7E0D3] bg-[#FFFDF7] p-0">
        <div className="p-6 pb-0">
          <DialogTitle className="font-display text-2xl text-[#6B1D2F]">Share with friends & family</DialogTitle>
          <DialogDescription className="mt-1 text-sm text-[#78716C]">Earn {pct}% referral rewards on eligible successful purchases.</DialogDescription>
        </div>
        <div data-testid="referral-share-card" className="mx-6 mt-5 overflow-hidden rounded-2xl border border-[#E5C378] bg-white shadow-md">
          {p.image && <img src={media(p.image)} alt={p.name} className="aspect-[16/10] w-full object-cover" />}
          <div className="maroon-pattern p-4 text-[#FFFDF7]">
            <p className="text-xs uppercase tracking-[0.2em] text-[#E5C378]">Bhakti Sakhi • Recommended by a Sakhi</p>
            {p.name && <p className="font-display mt-1 text-lg font-semibold">{p.name}</p>}
            {p.price && <p className="text-sm"><span className="font-bold">{inr(p.price)}</span> {p.mrp > p.price && <s className="ml-1 opacity-60">{inr(p.mrp)}</s>}</p>}
            <p className="mt-2 text-xs text-white/80">{msg}</p>
          </div>
        </div>
        <div className="p-6">
          {err ? <p className="rounded-xl bg-[#FDF2F4] p-3 text-sm text-[#6B1D2F]" data-testid="referral-error">{err}</p> : (
            <>
              <div className="flex items-center gap-2 rounded-xl border border-[#E7E0D3] bg-white p-2">
                <input readOnly value={link || "Generating your unique link…"} data-testid="referral-link-input" className="flex-1 truncate bg-transparent px-2 text-xs outline-none" />
                <button disabled={!link} onClick={() => copy(link, "Referral link copied")} data-testid="copy-referral-link-btn" className="btn-maroon !px-3 !py-1.5 text-xs"><Copy className="h-3.5 w-3.5" />Copy</button>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <button disabled={!link} data-testid="share-whatsapp-btn" onClick={() => open(`https://wa.me/?text=${encodeURIComponent(full)}`)} className="btn !rounded-2xl bg-[#25D366] text-white"><MessageCircle className="h-4 w-4" />WhatsApp</button>
                <button disabled={!link} data-testid="share-facebook-btn" onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`)} className="btn !rounded-2xl bg-[#1877F2] text-white"><Facebook className="h-4 w-4" />Facebook</button>
                <button disabled={!link} data-testid="share-instagram-btn" onClick={() => { copy(full, "Caption & link copied — paste it on Instagram"); open("https://instagram.com"); }} className="btn !rounded-2xl bg-gradient-to-r from-[#F58529] to-[#C13584] text-white"><Instagram className="h-4 w-4" />Instagram</button>
              </div>
              <p className="mt-4 flex items-start gap-2 text-[11px] text-[#78716C]"><Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#D97706]" />Rewards are credited only after a validated, eligible purchase is delivered. Link clicks alone do not earn rewards. T&C apply.</p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
